import { useCallback, useState, useEffect, ReactElement } from "react";
import { useDispatch, useSelector } from "react-redux";
import CleaveRoot from "cleave.js";
import Cleave from "cleave.js/react";
import { useDebounceCallback } from "@react-hook/debounce";

import { PhoneNumberFieldConfig } from "@evergiving/common/dist/es2015/form";

import { RootState } from "@/lib/redux/types";
import { setFormValue, resetFormErrors } from "@/lib/redux/actions";
import { LabelledField } from "@/lib/components/LabelledField";
import { LabelText } from "@/lib/components/LabelText";
import { InputFieldConfig } from "@/lib/misc/InputFieldConfig";
import { FieldError } from "@/lib/components/FieldError";

import {
  AsYouType,
  CountryCode,
  parsePhoneNumber,
  PhoneNumber,
} from "libphonenumber-js/core";
import { metadata } from "@/lib/config/libphonenumberJsMetadata"; // uses custom loader
import { useAutofill } from "@/lib/hooks/useAutofill";

class AsYouTypeFormatter extends AsYouType {
  constructor(country: CountryCode) {
    super(country, metadata);
  }

  clear() {
    this.reset();
  }

  inputDigit(digit: string) {
    return this.input(digit);
  }
}

(CleaveRoot as any).AsYouTypeFormatter = AsYouTypeFormatter;

function tryParsePhoneNumber(
  phoneNumber: string,
  countryCode: string | undefined
) {
  try {
    return parsePhoneNumber(phoneNumber, countryCode as CountryCode, metadata);
  } catch (e) {
    if (e instanceof Error) return e;
    return Error(String(e));
  }
}

function isValidPhoneNumber(
  phoneNumber: PhoneNumber | Error
): phoneNumber is PhoneNumber {
  return !(phoneNumber instanceof Error) && phoneNumber.isValid();
}

function isStringEmpty(s: string | undefined) {
  return !s || s.trim().length === 0;
}

export function PhoneNumberField({
  config,
  autoFocus,
}: {
  config: PhoneNumberFieldConfig;
} & InputFieldConfig): ReactElement {
  const name = "phoneNumber";
  const required = !!config.requiredFieldErrorText;
  const valueFromStore = useSelector((state: RootState) => state[name].value);
  const currentCountryCode = useSelector(
    (state: RootState) => state.countryCode.value
  );

  const initialPhoneNumber = tryParsePhoneNumber(
    valueFromStore || "",
    currentCountryCode
  );
  const [currentValue, setCurrentValue] = useState<string>(
    valueFromStore || ""
  );
  const [phoneNumber, setPhoneNumber] = useState<PhoneNumber | Error>(
    initialPhoneNumber
  );
  const dispatch = useDispatch();
  const [isDirty, setIsDirty] = useState(false);

  const errorMessages =
    // eslint-disable-next-line no-nested-ternary
    config.requiredFieldErrorText && isStringEmpty(valueFromStore)
      ? [config.requiredFieldErrorText]
      : isValidPhoneNumber(phoneNumber)
      ? []
      : [config.invalidNumberErrorText];

  const anyErrors = errorMessages.length > 0;

  const saveData = useCallback(
    (value: string) => {
      const newPhoneNumber = tryParsePhoneNumber(value, currentCountryCode);

      setPhoneNumber(newPhoneNumber);
      const prevValue = valueFromStore || "";
      const nextValue = value.replace(/[^0-9+]/g, "");

      if (nextValue !== "" || prevValue !== "") {
        dispatch(
          setFormValue(
            name,
            nextValue,
            isStringEmpty(value) || !isValidPhoneNumber(newPhoneNumber)
          )
        );
        setIsDirty(true);
      }
    },
    [dispatch, name, currentCountryCode, setPhoneNumber, valueFromStore]
  );

  const {
    detectAutofillByAnimation,
    handleFocusWithAutofill,
    handleInputWithAutofillSafariHack,
    saveDataWithAutofill,
  } = useAutofill({ saveData });

  const debouncedSaveData = useDebounceCallback(() => {
    saveData(currentValue);
  }, 1000);

  // Cleave does something to the original onChange that disrupts the autofill detection hack
  const handleChangeCapture = useCallback(
    ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
      saveDataWithAutofill(value);
    },
    [saveDataWithAutofill]
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      if (value) {
        setCurrentValue(value);
        dispatch(resetFormErrors(name));

        debouncedSaveData();
      }
    },
    [debouncedSaveData, dispatch]
  );

  useEffect(() => {
    saveData(valueFromStore || "");
  }, [saveData, valueFromStore, currentCountryCode]);

  const errorId = `${name}-errors`;

  const inputField = (
    <>
      {/* eslint-disable jsx-a11y/no-autofocus */}
      {/* ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^ Discussion of this override follows:

       We're only passing through the attribute here.  See DonationDetailsPage for
       a discussion why we would ever want to set this attribute.

       */}
      <div className="creditCardNumberContainer">
        <Cleave
          options={{
            phone: true,
            phoneRegionCode: currentCountryCode,
          }}
          className="textField"
          type="text"
          name="phoneNumber"
          placeholder={config.placeholderText}
          value={currentValue}
          onAnimationStart={detectAutofillByAnimation}
          onChange={handleChange}
          onChangeCapture={handleChangeCapture}
          onFocus={handleFocusWithAutofill}
          onInput={handleInputWithAutofillSafariHack}
          autoComplete="tel"
          inputMode="tel"
          autoFocus={autoFocus}
          spellCheck="false"
          onInit={(owner) => {
            // https://github.com/nosir/cleave.js/issues/601#issuecomment-902747682
            // eslint-disable-next-line no-param-reassign
            (owner as any).lastInputValue = "";
          }}
          aria-required={required}
          aria-invalid={(anyErrors && isDirty) || undefined}
          aria-describedby={anyErrors ? errorId : undefined}
        />
      </div>
      <div role="alert">
        {anyErrors && isDirty ? (
          <FieldError id={errorId} messages={errorMessages} />
        ) : null}
      </div>
    </>
  );

  return (
    <LabelledField id={name}>
      <LabelText requiredIndicator={required}>{config.labelText}</LabelText>
      {inputField}
    </LabelledField>
  );
}
